import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, combineLatestWith, map } from 'rxjs';
import { IApiResult } from 'common';
import { IDashboardData, IDashboardHeaderDataItem } from 'common';
import { IFinanceStatus } from 'common';
import { ICard } from 'root-layout';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly valueRegexp = new RegExp('({{(\\w+)}})');
  private readonly statusRegexp = new RegExp('(\\[\\[calculate_(\\w+)\\]\\])');
  private readonly cardValueRegexp = new RegExp('(\\[\\[value_(\\w+)\\]\\])');

  constructor(private httpClient: HttpClient) {}

  getBroadcastingDashboardData(): Observable<IDashboardData> {
    return this.httpClient.get<IApiResult<IDashboardData>>('/dashboard/accounts/:accountCode/broadcasting').pipe(
      combineLatestWith(this.httpClient.get<IApiResult<IFinanceStatus>>('/finance/accounts/:accountCode/status')),
      map(([ddResponse, finResponse]) => {
        this.mapFinanceResponseToDashboardData(ddResponse, finResponse);
        return ddResponse.value;
      })
    );
  }

  getRadioDashboardData(): Observable<IDashboardData> {
    return this.httpClient.get<IApiResult<IDashboardData>>('/dashboard/accounts/:accountCode/radio').pipe(
      combineLatestWith(this.httpClient.get<IApiResult<IFinanceStatus>>('/finance/accounts/:accountCode/status')),
      map(([ddResponse, finResponse]) => {
        this.mapFinanceResponseToDashboardData(ddResponse, finResponse);
        return ddResponse.value;
      })
    );
  }

  getPrsDashboardData(): Observable<IDashboardData> {
    return this.httpClient.get<IApiResult<IDashboardData>>('/dashboard/accounts/:accountCode/prs').pipe(
      combineLatestWith(this.httpClient.get<IApiResult<IFinanceStatus>>('/finance/accounts/:accountCode/status')),
      map(([ddResponse, finResponse]) => {
        this.mapFinanceResponseToDashboardData(ddResponse, finResponse);
        return ddResponse.value;
      })
    );
  }

  private mapFinanceResponseToDashboardData(ddResponse: IApiResult<IDashboardData>, finResponse: IApiResult<IFinanceStatus>) {
    const financeStatus = finResponse.value;
    ddResponse.value.header.data.map((dataItem) => {
      dataItem.value = this.replaceMatchedValue(dataItem.value, financeStatus);
    });
    ddResponse.value.cards.map((card) => {
      card.description = this.replaceMatchedValue(card.description, financeStatus);
    });

    this.setNumericStatus(ddResponse.value.header.data);
    this.setSpanWarningAndValue(ddResponse.value.cards);
  }

  private replaceMatchedValue(fullString: string, financialStatus: IFinanceStatus): string {
    const match = this.valueRegexp.exec(fullString);
    if (match && match.length === 3) {
      fullString = fullString.replace(match[1], financialStatus[match[2]]);

      //If prepaid funds is less then 0 then it should be shown on the dashboard as 0
      //We did change this on the API, but it broke other uses of the data where the negative value is needed.
      if (match[2] === 'prepaidFunds' && +financialStatus[match[2]] < 0) {
        fullString = '0';
      }
    }
    return fullString;
  }

  private setNumericStatus(dashboardDataItems: IDashboardHeaderDataItem[]): void {
    for (let dataItem of dashboardDataItems) {
      const match = this.statusRegexp.exec(dataItem.valueStatus);
      if (match && match.length === 3 && !isNaN(+dataItem.value)) {
        dataItem.valueStatus =
          +dataItem.value === 0
            ? 'neutral'
            : +dataItem.value > 0
              ? match[2] === 'positive'
                ? 'positive'
                : 'negative'
              : match[2] === 'negative'
                ? 'positive'
                : 'negative';
      }
    }
  }

  private setSpanWarningAndValue(cards: ICard[]): void {
    for (let card of cards) {
      const statusMatch = this.statusRegexp.exec(card.description);
      if (statusMatch && statusMatch.length === 3) {
        const valueMatch = this.cardValueRegexp.exec(card.description);
        const cardValue = +valueMatch[2];
        const warningString =
          cardValue === 0
            ? ''
            : cardValue > 0
              ? statusMatch[2] === 'positive'
                ? ''
                : '--warning'
              : statusMatch[2] === 'negative'
                ? ''
                : '--warning';
        card.description = card.description.replace(statusMatch[1], warningString).replace(valueMatch[1], valueMatch[2]);
      }
    }
  }
}
